import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  BUSINESS_FIELDS,
  US_COUNTRY_CODE,
} from 'components/BusinessDetails/constants';
import { getAllFormErrors, getErrorMessage } from 'lib/fieldValidation';
import { selectGmb } from 'lib/googleAutocomplete';
import objectIsEmpty from 'lib/objectIsEmpty';

import { formatBusinessCountryCode } from '../components/BusinessDetails';

export const BusinessInfoContext = createContext({});

export const useBusinessInfoContext = () => useContext(BusinessInfoContext);

const initBusinessFields = [...BUSINESS_FIELDS].reduce((fields, { field }) => {
  fields[field] = field === 'country' ? US_COUNTRY_CODE : '';
  return fields;
}, {});

// easily pass & update business info
const BusinessInfoContextProvider = ({ children }) => {
  const [businessInfo, setBusinessInfo] = useState(initBusinessFields);
  const [errors, setErrors] = useState({});
  const [gmb, setGmb] = useState({});
  const [showManualForm, setShowManualForm] = useState(false);

  const { name: businessName } = businessInfo;

  const resetErrors = useCallback(
    () => setErrors({}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const checkForBusinessInfoErrors = (
    businessInfo,
    optionalFields,
    displayErrors,
    trackEvent,
    trackEventPath,
    trackEventNamespace,
    trackEventType
  ) => {
    const businessErrors = getAllFormErrors({
      formFields: [...BUSINESS_FIELDS],
      formValues: businessInfo,
      optionalFields,
      trackEvent,
      trackEventPath,
      trackEventNamespace,
      trackEventType,
    });

    const isValid = objectIsEmpty(businessErrors);

    if (!isValid) {
      displayErrors && setErrors(businessErrors);
      // display business errors if present & name has been filled
      // but form is not expanded
      if (businessName && !showManualForm) {
        setShowManualForm(true);
      }
    } else {
      setErrors({});
    }

    return isValid;
  };

  const resetBusinessInfo = useCallback(
    () => setBusinessInfo(initBusinessFields),
    []
  );

  const validateBusinessInfo = useCallback(
    ({
      displayErrors = true,
      optionalFields = ['phone'],
      trackEvent,
      trackEventPath = 'trials.businessInfo.fieldError',
      trackEventNamespace = 'signup',
      trackEventType = 'businessInfo',
    } = {}) => {
      return checkForBusinessInfoErrors(
        businessInfo,
        optionalFields,
        displayErrors,
        trackEvent,
        trackEventPath,
        trackEventNamespace,
        trackEventType
      );
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businessInfo]
  );

  const validateBusinessName = useCallback(
    (displayErrors) => {
      const businessNameErrorMessage = getErrorMessage('name', businessName);
      const isValid = !businessNameErrorMessage;

      if (displayErrors) {
        setErrors({
          ...errors,
          name: businessNameErrorMessage,
        });
      }

      return isValid;
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businessInfo]
  );

  const handleGmbPredictionSelection = useCallback(
    (selectedGmb = {}, callback, trackEvent) => {
      selectGmb(selectedGmb, setGmb)
        .then((formattedGmb) => {
          if (formattedGmb) {
            const gmbBusiness = { ...businessInfo, ...formattedGmb };
            setBusinessInfo(gmbBusiness);
            const optionalFields = ['phone'];
            const displayErrors = true;
            const trackEventPath = 'trials.businessInfo.fieldError';
            const trackEventNamespace = 'signup';
            const trackEventType = 'businessInfo';
            checkForBusinessInfoErrors(
              gmbBusiness,
              optionalFields,
              displayErrors,
              trackEvent,
              trackEventPath,
              trackEventNamespace,
              trackEventType
            );
          }
        })
        .finally(() => {
          callback && callback();
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businessInfo]
  );

  const address = useMemo(() => {
    const { address, addressOptional, city, country, state, zip } =
      businessInfo || {};

    return {
      addressLines: `${address} ${addressOptional || ''}`,
      administrativeArea: state,
      country: formatBusinessCountryCode(country),
      locality: city,
      postalCode: zip,
    };
  }, [businessInfo]);

  const businessData = useMemo(
    () => ({
      address,
      businessInfo,
      businessName,
      errors,
      gmb,
      handleGmbPredictionSelection,
      resetErrors,
      resetBusinessInfo,
      setBusinessInfo,
      setErrors,
      setGmb,
      setShowManualForm,
      showManualForm,
      validateBusinessInfo,
      validateBusinessName,
    }),
    [
      address,
      businessInfo,
      businessName,
      errors,
      gmb,
      handleGmbPredictionSelection,
      resetBusinessInfo,
      resetErrors,
      showManualForm,
      validateBusinessInfo,
      validateBusinessName,
    ]
  );

  return (
    <BusinessInfoContext.Provider value={businessData}>
      {children}
    </BusinessInfoContext.Provider>
  );
};

export default BusinessInfoContextProvider;
